export interface IEventMap {
  modulesLoaded: void
  // Add more events here, e.g.:
  // userLoggedIn: { userId: string };
}

/**
 *
 */
export class EventBus<TEvents extends Record<string, any>> {
  /**
   * List of current listeners
   * @private
   */
  private listeners: {
    [K in keyof TEvents]?: Array<(payload?: TEvents[K]) => void>
  } = {}

  /**
   * In case an event is emitted BEFORE being listened to,
   * I put it in the buffer
   * @private
   */
  private listenersBuffer: {
    [K in keyof TEvents]?: TEvents[K]
  } = {}

  /**
   * Add a listener with an handler/callback to be triggered
   *
   * @param event
   * @param handler
   */
  on<K extends keyof TEvents>(
    event: K,
    handler: (payload?: TEvents[K]) => void,
  ): void {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }

    // Create the listener that will be fired\triggered
    this.listeners[event].push(handler)

    // Immediately call the handler if this event has been emitted before
    if (Object.prototype.hasOwnProperty.call(this.listenersBuffer, event)) {
      handler(this.listenersBuffer[event])
    }
  }

  /**
   * Remove a specific listener (handler that was used in the "on" needed
   * to catch and remove only the correct listener)
   *
   * @param event
   * @param handler
   */
  off<K extends keyof TEvents>(
    event: K,
    handler?: (payload: TEvents[K]) => void,
  ): void {
    if (!this.listeners[event]) {
      return
    }

    if (!handler) {
      delete this.listeners[event]
    } else {
      this.listeners[event] = this.listeners[event]!.filter(
        (h) => h !== handler,
      )
    }
  }

  /**
   * Emit an event, optionally buffering it
   *
   * @param event
   * @param buffer
   * @param payload
   */
  emit<K extends keyof TEvents>(
    event: K,
    buffer = true,
    payload?: TEvents[K],
  ): void {
    // Save the latest payload (if buffer is required)
    if (buffer) {
      this.listenersBuffer[event] = payload
    }

    // If a listener is already set, trigger it
    const handlers = this.listeners[event]
    if (handlers) {
      for (const handler of handlers) {
        if (payload) {
          handler(payload)
        } else {
          handler()
        }
      }
    }
  }

  /**
   * Optional: Clear the buffer for a specific event
   * @param event
   */
  clear<K extends keyof TEvents>(event: K): void {
    delete this.listenersBuffer[event]
  }
}

/**
 *
 */
export const eventBus = new EventBus()
